// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/*
 This file collects various GUI utility functions used throughout the frontend GUI application.
 Put functions here only if they are used in multiple components and not worth putting into
 a separate model or specific class file.
*/

import * as React from "react";
import type { JSX } from "react";
import { Dropdown } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DateTime, DurationLike } from "luxon";
import { createSelector } from "@reduxjs/toolkit";

import { isSet, roundToDigits } from "shared/utils/utils";
import { DeletionStrategy, IVirtAssign, Report } from "shared/types/cloudtypes";
import { DeviceType } from "shared/types/globtypes";
import { EnergySource, EnergyUnit, InputType, OutputMode, PowerUnit } from "shared/types/parameter";
import {
  DashboardPanel,
  DataState,
  EnumTypeString,
  IDashboardEntry,
  INavigationPanelEntry,
  MainPanel,
  PeriodIdentfier,
  RootPath,
  Theme,
  TimePeriod,
} from "types";
import { Aggregation } from "graphql/api";
import { ActionType, IActionSortTable, IActionTypeSetPeriod } from "actions";
import { IState } from "states/states";
import { IDateRange, IPeriodData, ITableSort } from "states/guistates";
import { Category, GraphViewMode, ISiteSelection } from "states/cachedstates";
import { Site } from "states/sitestates";
import { IStateController } from "states/controllerstates";
import { AppState } from "states/schedulerstates";

import IconVirtualSite from "icons/iconLink";

import moduleConfig from "shared/modules/enplanconfig.json";

// uses webpack definitions
declare const IS_CLOUD: boolean;

export const MAX_DASHBOARDS = 6;

export const COLOR_SELECT = "#66C0CC"; // "#70adbc"

// must match the Cognito user pool password policy
export const MIN_PASSWORD_LENGTH = 8;

export function isValidEmail(email: string): boolean {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email !== "" && regex.test(email);
}

const DEFAULT_COLORS = [
  "#55c65d",
  "#3366CC",
  "#FF9900",
  "#109618",
  "#990099",
  "#3B3EAC",
  "#0099C6",
  "#66AA00",
  "#316395",
  "#994499",
  "#22AA99",
  "#AAAA11",
  "#6633CC",
  "#E67300",
  "#329262",
  "#5574A6",
  "#3B3EAC",
];

const PALE_COLORS = [
  "#55c65d44",
  "#3366CC44",
  "#FF990044",
  "#10961844",
  "#99009944",
  "#3B3EAC44",
  "#0099C644",
  "#66AA0044",
  "#31639544",
  "#99449944",
  "#22AA9944",
  "#AAAA1144",
  "#6633CC44",
  "#E6730044",
  "#32926244",
  "#5574A644",
  "#3B3EAC44",
];

export function getColor(index: number | undefined, showGrayScale = false) {
  if (showGrayScale === true) {
    return PALE_COLORS[(index ?? 0) % DEFAULT_COLORS.length];
  } else {
    return DEFAULT_COLORS[(index ?? 0) % DEFAULT_COLORS.length];
  }
}

export function parseHtmlInputValue(
  inputElement: HTMLInputElement,
): string | boolean | number | Date {
  if (inputElement.type === "checkbox") {
    return inputElement.checked;
  } else if (inputElement.type === "date" && isSet(inputElement.valueAsDate)) {
    return inputElement.valueAsDate;
  } else if (inputElement.type === "number") {
    return inputElement.valueAsNumber;
  } else {
    return inputElement.value;
  }
}

export function setInputField(
  inStorage: any,
  inName: string,
  value: string | boolean | number | Date | Record<string, unknown> | undefined | null,
) {
  let isNestedElementModified = false;
  // determine storage element
  let storage = inStorage as { [key: string]: unknown }; // per default, we directly use the provided storage
  let name = inName; // per default, we directly use provided member name
  // remove "]" characters
  name = name.replace("]", "");
  // is name element still nested?
  while (name.includes(".") || name.includes("[")) {
    // we face a nested element
    // array or sub-object?
    const isObj =
      !name.includes("[") || (name.includes(".") && name.indexOf(".") < name.indexOf("["));
    const member = name.split(/[.[]/)[0];
    // recreate this sub-node object to ensure screen updates
    if (isObj) {
      storage[member] =
        storage.hasOwnProperty(member) ?
          { ...(storage[member] as { [key: string]: unknown }) }
        : {};
    } else {
      storage[member] = Array.isArray(storage[member]) ? [...(storage[member] as [unknown])] : [];
    }
    storage = storage[member] as { [key: string]: unknown };
    name = name.substring(member.length + 1);
    // take this new element as the new basis
    isNestedElementModified = true;
  }

  // check for matching data and storage type
  if (typeof storage[name] === typeof value) {
    storage[name] = value;
  } else if (!isSet(storage[name])) {
    storage[name] = value;
  } else if (!isSet(value)) {
    storage[name] = value;
  } else {
    console.warn(
      `unsupported member types in setInputField of ${name}: ${typeof storage[
        name
      ]} / ${typeof value}`,
    );
    storage[name] = value;
  }
  return isNestedElementModified;
}

// string enums have only a single map of values
export function stringEnumToLabels<TEnum extends string>(enumType: EnumTypeString<TEnum>): TEnum[] {
  return Object.values(enumType) as TEnum[];
}

export function toDropdownItems<ItemType>(
  valueList: ItemType[],
  name: string,
  mapperFunction: (name: string, value: ItemType, event?: React.MouseEvent) => void,
  labelFunction?: (value: ItemType) => string | JSX.Element,
  selectedType?: number,
): JSX.Element[] {
  const propertyName = name.substring(name.lastIndexOf(".") + 1);
  const dropdownItems = valueList.map((curValue) => {
    return (
      <Dropdown.Item
        key={`${curValue}${name}`}
        onClick={(event) => {
          (selectedType === undefined || selectedType !== curValue) &&
            mapperFunction?.(name, curValue, event);
        }}
      >
        {labelFunction ?
          labelFunction(curValue)
        : <FormattedMessage id={`${propertyName}_${curValue}`} />}
      </Dropdown.Item>
    );
  });
  return dropdownItems;
}

export function hexToRgba(hex: string, alpha: number) {
  // test for valid input string
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let colorStr: string[] = hex.substring(1).split("");
    if (colorStr.length === 3) {
      // short form, e.g. #fff, #000
      // extend to long form
      colorStr = [colorStr[0], colorStr[0], colorStr[1], colorStr[1], colorStr[2], colorStr[2]];
    }
    const colorCode = parseInt(colorStr.join(""), 16);
    const colorArr: number[] = [(colorCode >> 16) & 255, (colorCode >> 8) & 255, colorCode & 255];
    colorArr.push(alpha);
    return "rgba(" + colorArr.join(",") + ")";
  } else {
    console.warn("cannot convert color " + hex);
    return hex;
  }
}

export function isStateDirty(state: DataState) {
  return state === DataState.DIRTY || state === DataState.SAVE_REQ || state === DataState.SAVE_FAIL;
}

export function getTimezoneDiff(deviceTimezoneOffset: number | undefined): number {
  // returns the timezone difference from device to browser in s
  if (!isSet(deviceTimezoneOffset)) {
    return 0;
  }
  // positive difference if local timezone is later than device timezone
  return -getBrowserTimezoneOffset() - deviceTimezoneOffset;
}

export function getBrowserTimezoneOffset(): number {
  // returns the browser timezone offset in s
  return 60 * new Date().getTimezoneOffset();
}

// timestamp in ms or as Date
export function getLocalDate(timestamp: Date | number | undefined) {
  if (isSet(timestamp)) {
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      // pad year: <input type="date"> rejects e.g. "2-01-01" while the user types the year
      const dateStr = `${`${date.getFullYear()}`.padStart(4, "0")}-${`0${date.getMonth() + 1}`.slice(
        -2,
      )}-${`0${date.getDate()}`.slice(-2)}`;
      return dateStr;
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date; locale-formatted date for display in UI text (e.g. error
// messages) - unlike getLocalDate, which returns ISO "YYYY-MM-DD" as required by
// <input type="date"> regardless of locale
export function getDisplayDate(
  timestamp: Date | number | undefined,
  locale: string | string[] | undefined,
): string {
  if (isSet(timestamp)) {
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toLocaleDateString(locale);
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date
export function getLocalTime(timestampMs: Date | number | undefined): string {
  if (isSet(timestampMs)) {
    try {
      // we need to construct the new Date() at the current day, otherwise the
      // hours might be wrong due to winterzeit/sommerzeit
      const dateOffset = Date.now() - (Date.now() % (24 * 3600 * 1000));
      const date =
        timestampMs instanceof Date ? timestampMs : (
          new Date(timestampMs + (timestampMs < 24 * 3600 * 1000 ? dateOffset : 0))
        );
      const dateStr = `${`0${date.getHours()}`.slice(-2)}:${`0${date.getMinutes()}`.slice(
        -2,
      )}:${`0${date.getSeconds()}`.slice(-2)}`;
      return dateStr;
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date; if undefined, return empty string
export const getLocalDateTime = (timestampMs: Date | number | undefined): string => {
  return `${getLocalDate(timestampMs)} ${getLocalTime(timestampMs)}`;
};

// timestamp in ms or as Date; if undefined, get current time
export const getLocalDateTimeIso = (timestampMs: Date | number | undefined): string => {
  if (isSet(timestampMs)) {
    // local getters use the offset of that date (not today's), e.g. for summer/winter time
    const date = timestampMs instanceof Date ? timestampMs : new Date(timestampMs);
    return `${getLocalDate(date)}T${getLocalTime(date)}`;
  } else {
    return "";
  }
};

export function getPeriodStart(
  selectedDate: number,
  zoom: TimePeriod | undefined,
  timezoneOffset: number,
) {
  const timezoneDifference = getTimezoneDiff(timezoneOffset);

  // the calculation date is shifted in timezone
  const date = new Date((selectedDate - timezoneDifference) * 1000);

  // fix hours/minutes/...
  date.setHours(0, 0, 0, 0);
  // fix date
  if (zoom === TimePeriod.YEAR) {
    date.setDate(1);
    date.setMonth(0);
  } else if (zoom === TimePeriod.MONTH) {
    date.setDate(1);
  } else if (zoom === TimePeriod.WEEK) {
    // getDay has 0 for Sunday but we want to start the week on Monday
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  }
  return date.getTime() / 1000 + timezoneDifference;
}

// describes the deletion strategy of the trend data table - refer to dbcleanup.ts for details.
// shared between guireducer.ts (which enforces it when a period is applied) and any UI that
// needs to preview the same boundary (e.g. ModalTimeFilter's summary) - keep in sync with
// dbcleanup.ts if the retention rules ever change.
export function getDeletionBoundary(rootState: IState) {
  // get database model
  const deletionStrategy = rootState.site.siteList?.find(
    (curSite) => curSite.id === rootState.cached.selectedSite,
  )?.cloudSettings?.deletionStrategy;

  // in reality, aggregation always starts after 2 years, for all models
  // however, due to licensing, we restrict access to non-aggregated data earlier
  // const aggregationDuration: DurationLike | undefined = { years: 2 };
  const aggregationDuration: DurationLike | undefined =
    deletionStrategy === DeletionStrategy.SMALL ? { months: 6 }
    : deletionStrategy === DeletionStrategy.LARGE ? { years: 1 }
    : { years: 2 };
  // the controller keeps all raw data, so there is no boundary there
  const aggregationBoundary =
    IS_CLOUD ? DateTime.now().minus(aggregationDuration).startOf("month") : undefined;
  // we always allow to show all data, but only aggregated
  const deletionBoundary = undefined as DateTime | undefined;
  return { deletionBoundary, aggregationBoundary };
}

export function getGraphBoundaryDates(
  graphStartDate: number | undefined,
  graphEndDate: number | undefined,
  timezoneOffset: number | undefined,
  graphZoom: TimePeriod | undefined,
) {
  // derive timezoneoffset
  if (!isSet(timezoneOffset)) {
    timezoneOffset = -getBrowserTimezoneOffset();
  }
  // if we zoom to all data, we provide undefined timestamps
  if (graphZoom === TimePeriod.ALL) {
    return {
      startDate: undefined,
      endDate: undefined,
      timezoneOffset,
    };
  }

  // we need to clone the start date to be able to detect changes for screen updates
  let startDate: number | undefined;
  if (graphStartDate) {
    // clone date
    startDate = graphStartDate;
  } else {
    // as a fallback, take browser date
    startDate = Date.now() / 1000;
  }
  // adjust to zoom limits
  startDate = getPeriodStart(startDate, graphZoom, timezoneOffset);
  // if no endDate is available -> use the (exclusive) end of the current period
  const endNow =
    graphZoom === TimePeriod.YEAR ? DateTime.now().startOf("year").plus({ years: 1 })
    : graphZoom === TimePeriod.MONTH ? DateTime.now().startOf("month").plus({ months: 1 })
    : graphZoom === TimePeriod.WEEK ? DateTime.now().startOf("week").plus({ weeks: 1 })
    : DateTime.now().startOf("day").plus({ days: 1 });
  const endDate = isSet(graphEndDate) ? graphEndDate : endNow.toSeconds();

  return {
    startDate,
    endDate,
    timezoneOffset,
  };
}

export function roundTimestamp(timestamp: number) {
  return Math.round(timestamp / 60) * 60;
}

export const FORMAT_NON_KILO_LIMIT = 1.0; // below this value, the number will be expressed in W instead of kW

export function doKiloConversion(value: number | null | undefined) {
  return isSet(value) && value !== 0 && Math.abs(value) < FORMAT_NON_KILO_LIMIT;
}

export function getPowerUnit(unit: PowerUnit | undefined, doConversion: boolean) {
  if (doConversion) {
    return (
      unit === PowerUnit.KWH ? "W"
      : unit === PowerUnit.QM ? "dm³/h"
      : unit === PowerUnit.L ? "ml/h"
      : unit === PowerUnit.KG ? "g/h"
      : unit === PowerUnit.KVARH ? "var"
      : unit === PowerUnit.NONE ? `milli/h`
      : "milli"
    );
  }
  return unit === PowerUnit.NONE ? "/h" : unit;
}

export function getEnergyUnit(unit: EnergyUnit | PowerUnit | undefined, doConversion: boolean) {
  if (doConversion) {
    return (
      unit === EnergyUnit.KWH || unit === PowerUnit.KWH ? "Wh"
      : unit === EnergyUnit.QM || unit === PowerUnit.QM ? "dm³"
      : unit === EnergyUnit.L || unit === PowerUnit.L ? "ml"
      : unit === EnergyUnit.KG || unit === PowerUnit.KG ? "g"
      : unit === EnergyUnit.KVARH || unit === PowerUnit.KVARH ? "varh"
      : unit === EnergyUnit.NONE || unit === PowerUnit.NONE ? "milli"
      : "milli"
    );
  }
  return (
    unit === EnergyUnit.KWH || unit === PowerUnit.KWH ? "kWh"
    : unit === EnergyUnit.QM || unit === PowerUnit.QM ? "m³"
    : unit === EnergyUnit.L || unit === PowerUnit.L ? "l"
    : unit === EnergyUnit.KG || unit === PowerUnit.KG ? "kg"
    : unit === EnergyUnit.KVARH || unit === PowerUnit.KVARH ? "kvarh"
    : unit === EnergyUnit.NONE || unit === PowerUnit.NONE ? ""
    : ""
  );
}

export function formatPowerValue(value: number | null | undefined, unit: PowerUnit | undefined) {
  if (!isSet(value) || isNaN(value)) {
    return "-"; // empty string? 0? "-"?
  }

  // check whether this value should be reported in W instead of kW
  const doConversion = doKiloConversion(value);
  const scaledVal = doConversion ? value * 1000 : value;
  const scaledUnit = getPowerUnit(unit, doConversion);
  return `${roundToDigits(scaledVal, 2)} ${scaledUnit}`;
}

export function formatEnergyValue(
  value: number | null | undefined,
  unit: EnergyUnit | PowerUnit | undefined,
) {
  if (!isSet(value) || isNaN(value)) {
    return "-"; // empty string? 0? "-"?
  }

  // check whether this value should be reported in W instead of kW
  const doConversion = doKiloConversion(value);
  const scaledVal = doConversion ? value * 1000 : value;
  const scaledUnit = getEnergyUnit(unit, doConversion);
  return `${roundToDigits(scaledVal, 2)} ${scaledUnit}`;
}

export const parseDateTimeEvent = (
  event: React.FormEvent<HTMLInputElement>,
  oldTimestamp: number | undefined,
  timezoneOffset?: number,
): number | undefined => {
  // for <input type="time">; date inputs use the DateInput component
  const newTimestamp = parseHtmlInputValue(event.currentTarget);
  let utcTimestamp: number | undefined;
  if (event.currentTarget.name === "time") {
    // we need to construct the new Date() at the current day, otherwise the
    // hours might be wrong due to winterzeit/sommerzeit
    const dateOffset =
      (oldTimestamp ?? 0) < 24 * 3600 * 1000 ? Date.now() - (Date.now() % (24 * 3600 * 1000)) : 0;
    const oldDate =
      isSet(oldTimestamp) ?
        new Date(oldTimestamp + dateOffset - getTimezoneDiff(timezoneOffset))
      : new Date();
    const timestring = newTimestamp as string;
    const newDate = new Date(
      oldDate.getFullYear(),
      oldDate.getMonth(),
      oldDate.getDate(),
      parseInt(timestring.substring(0, 2)),
      parseInt(timestring.substring(3, 5)),
      parseInt(timestring.substring(6, 8)) || 0, // take care: in some browsers (Chrome) the string may omitt seconds
    );
    utcTimestamp = newDate.getTime() + getTimezoneDiff(timezoneOffset);
  } else {
    console.warn("unsupported time event", event.currentTarget.name);
  }

  if (!utcTimestamp || isNaN(utcTimestamp)) {
    console.warn("parsed illegal timestamp", newTimestamp);
    return undefined;
  }
  return utcTimestamp;
};

export function makeSortHeader(
  table: string,
  column: string,
  caption: JSX.Element,
  curSort: ITableSort | undefined,
  onSortAction: (sort: IActionSortTable) => void,
  addClasses?: string[],
  title?: string,
  colSpan?: number,
) {
  const classes = addClasses ?? [];
  if (curSort?.column === column) {
    classes.push(curSort.isSortAscending ? "sort-asc" : "sort-desc");
  } else {
    classes.push("sort");
  }
  return (
    <th
      className={classes.join(" ")}
      title={title}
      onClick={() =>
        onSortAction({
          table,
          column,
          isSortAscending: column !== curSort?.column ? true : !curSort?.isSortAscending,
        })
      }
      colSpan={colSpan}
    >
      {caption}
    </th>
  );
}

// add or remove the given ids to/from a checkbox selection
export function setSelectedIds(selectedIds: number[], ids: number[], isSelected: boolean) {
  const otherIds = selectedIds.filter((id) => !ids.includes(id));
  return isSelected ? [...otherIds, ...ids] : otherIds;
}

export function tabClickHandler(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key === "Enter") {
    event.currentTarget.click();
  }
}

export function isOcppFrontend(rootPath: RootPath | undefined) {
  // we use the /ocpp path and EP uses mobile.enline.at
  return (
    IS_CLOUD && (rootPath === RootPath.OCPP || window.location.hostname === "mobile.enline.at")
  );
}

export function isLocalhost() {
  return Boolean(
    window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
  );
}

export function getTheme() {
  switch (window.location.hostname) {
    case "enline.embyt.com":
    case "empower.embyt.com":
    case "localhost":
    default:
      return Theme.EMBYT;
    case "cloud.enline.at":
    case "mobile.enline.at":
    case "enline.cloud":
    case "www.enline.cloud":
    case "dev.embyt.com":
      return Theme.EPM;
  }
}

export function makeCommandSeqNr(timestampMs?: number) {
  // is timestamp given or use current time?
  const value = timestampMs ?? Date.now();
  // full ms is too long for type int, so take some module
  return value % (1 << 30);
}

function getBrowserName() {
  const userAgent = navigator.userAgent;
  let browserName;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "edge";
  } else {
    browserName = "unsupported Browser (" + userAgent + ")";
  }

  return browserName;
}

export function isSafariBrowser() {
  return getBrowserName() === "safari";
}

export function getIoType(state: IState) {
  if (isSet(state.gui.selectedIoType)) {
    return state.gui.selectedIoType;
  }
  // do we have digital inputs or outputs?
  if (
    state.para.parameter?.output.some(
      (curItem) => curItem.webActive && curItem.mode !== OutputMode.ANALOG,
    ) ||
    state.para.parameter?.input.some(
      (curItem) => curItem.webActive && curItem.type !== InputType.ANALOG,
    )
  ) {
    return "digital";
  }
  return "analog";
}

export function getTrendIoGraphViewMode(
  selection: ISiteSelection | undefined,
  selectedIoType: "digital" | "analog",
  allDataAvailable: boolean,
  prepareTrendData: boolean,
): GraphViewMode | undefined {
  let graphViewMode: GraphViewMode | undefined = selection?.graphViewMode;

  // if the user selected TABLE-View --> just leave the setting as is...
  if (graphViewMode !== GraphViewMode.TABLE) {
    // check if we prepare for TrendData or IO-Data
    if (prepareTrendData) {
      // on TrendData -> HORIZONTAL is not allowed
      if (graphViewMode === GraphViewMode.DIGITAL) {
        // use BAR instead
        graphViewMode = GraphViewMode.BAR;
      }
    } else {
      // otherwise -> check which Type we should show:
      if (selectedIoType === "analog") {
        // on ANALOG signals -> we just show LINE-Chart
        // independent on aggregated data or full!
        graphViewMode = GraphViewMode.LINE;
      } else {
        // on DIGITAL signals
        // check if we got all data:
        if (allDataAvailable) {
          // yes -> so show the horizontal (timeline-)view
          graphViewMode = GraphViewMode.DIGITAL;
        } else {
          // otherwise -> check which setting currently is used:
          // allowed values: BAR or LINE -> if the user switched from
          // all data (e.g. DAY) to aggregated data (e.g. MONTH), the setting
          // might be HORIZONTAL -> then we should switch to BAR (default)
          if (graphViewMode === GraphViewMode.DIGITAL) {
            graphViewMode = GraphViewMode.BAR;
          }
          // otherwise it is LINE or BAR already -> both are allowd, so levae them
        }
      }
    }
  }
  return graphViewMode;
}

export function getAggregationTimeUnitUINames(aggregation: Aggregation): string {
  switch (aggregation) {
    case Aggregation.MONTH:
      return "monthly";
    case Aggregation.DAY:
      return "daily";
    case Aggregation.HOUR:
      return "hourly";
    case Aggregation.NONE:
      return "minutely";
    default:
      throw new Error(`unexpected aggregation: ${aggregation}`);
  }
}

export function getDateRangeFromPeriod(inPeriod: IPeriodData | IActionTypeSetPeriod): IDateRange {
  const timePeriod = inPeriod.timePeriod ?? TimePeriod.DAY;
  const newStartDate = inPeriod.selectedDate ?? DateTime.now().startOf("day").toSeconds();

  if (timePeriod === TimePeriod.ALL) {
    return { startDate: Math.round(newStartDate), endDate: undefined, timePeriod };
  }

  // calc end-date
  let newEndDate = DateTime.fromSeconds(newStartDate);
  switch (timePeriod) {
    case TimePeriod.DAY:
      newEndDate = newEndDate.plus({ days: 1 });
      break;
    case TimePeriod.WEEK:
      newEndDate = newEndDate.plus({ weeks: 1 });
      break;
    case TimePeriod.MONTH:
      newEndDate = newEndDate.plus({ months: 1 });
      break;
    case TimePeriod.YEAR:
      newEndDate = newEndDate.plus({ years: 1 });
      break;
    case TimePeriod.CUSTOM:
      newEndDate = newEndDate.plus({ days: inPeriod.customDaysSelected ?? 1 });
      break;
  }

  return {
    startDate: Math.round(newStartDate),
    endDate: Math.round(newEndDate.toSeconds()),
    timePeriod: inPeriod.timePeriod ?? TimePeriod.DAY,
  };
}

// single entry point for deriving graph boundary dates from a period selection.
// use this (not getDateRangeFromPeriod / getGraphBoundaryDates directly) whenever
// a TimePeriod selection (IPeriodData / IActionTypeSetPeriod) is available.
// zoom defaults to the period's own timePeriod; pass zoomOverride if the graph
// boundary needs to be computed for a different zoom (e.g. a fixed dashboard
// zoom, or a per-item configured period)
//
// returns a single flat structure:
// - selectedStart / selectedEnd / timePeriod: the raw, pre-timezone-adjustment
//   values, exactly as selected - only needed when that raw value itself matters
//   (e.g. as a fallback anchor when graphStart/graphEnd is intentionally unset)
// - graphStart / graphEnd / timezoneOffset: the actual graph boundary - timezone
//   adjusted and TimePeriod.ALL aware - what most callers want
//
// only call getDateRangeFromPeriod / getGraphBoundaryDates directly when there is no
// period selection to begin with (e.g. an arbitrary timestamp range already in hand,
// such as the min/max timestamp of the available data)
export function getGraphBoundaryDatesFromPeriod(
  inPeriod: IPeriodData | IActionTypeSetPeriod,
  timezoneOffset: number | undefined,
  zoomOverride?: TimePeriod,
) {
  const dateRange = getDateRangeFromPeriod(inPeriod);
  const graphDates = getGraphBoundaryDates(
    dateRange.startDate,
    dateRange.endDate,
    timezoneOffset,
    zoomOverride ?? dateRange.timePeriod,
  );
  return {
    timePeriod: dateRange.timePeriod,
    selectedStart: dateRange.startDate,
    selectedEnd: dateRange.endDate,
    graphStart: graphDates.startDate,
    graphEnd: graphDates.endDate,
    timezoneOffset: graphDates.timezoneOffset,
  };
}

export const getSite = (state: IState) =>
  state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);

const getUserAccess = (state: IState) =>
  state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId)?.accessRights;

// the user may access any of the site settings
export const getCanModifySiteSettings = (state: IState) => {
  const access = getUserAccess(state);
  return Boolean(
    access?.modifySites ||
    access?.modifyAlarms ||
    access?.importTrendData ||
    access?.openSshTunnel ||
    access?.setVirtualParameters ||
    access?.crudTrendMonitors,
  );
};

// the user may recursively change all sites belonging to their company and subsidiaries
export const getCanModifySites = (state: IState) => Boolean(getUserAccess(state)?.modifySites);

// the connected site has no live data connection, also if it never had one
export const getIsSiteOffline = (state: IState) =>
  state.scheduler.appState >= AppState.READY &&
  (state.scheduler.isOffline ||
    (getExpectsLiveData(state) && !isSet(state.scheduler.allData.lastData)));
// live data are only received for a controller site in the unrestricted frontend;
// RFID users, OCPP/trend frontends and virtual sites never get any
const getExpectsLiveData = (state: IState) =>
  !state.site.user?.isRfidUser &&
  !isOcppFrontend(state.router.rootPath) &&
  state.router.rootPath !== RootPath.TREND &&
  getSite(state)?.deviceType !== DeviceType.VIRTUAL;
const getHasOcppChargers = (state: IState) =>
  state.ocpp.charger.filter((curItem) => isSet(curItem.fk_charger)).length > 0;
// inactive chargers are not shown, e.g. the LIS charger of each EZA controller
const getHasEnlineChargerData = (state: IState) =>
  (state.para.parameter?.charger.some((curItem) => curItem.isEnabled !== false) ?? false) &&
  (state.data?.charger?.length ?? 0) > 0;

/*
  Reports belong to a user and are either bound to a single site or, for the "site" type,
  span all sites of a company. We only show the reports of the currently selected site,
  plus the cross-site ones, which are independent of the site selection.
*/
export function getSiteReports(
  reportList: Report[] | undefined,
  siteId: number | undefined,
  isOcppRestricted: boolean,
) {
  // OCPP restricted frontends only deal with charger and rfid reports
  return (reportList ?? []).filter(
    (curItem) =>
      isSet(curItem.siteId) &&
      curItem.siteId === siteId &&
      (!isOcppRestricted || ["charger", "rfid"].includes(curItem.type)),
  );
}

export function getCrossSiteReports(reportList: Report[] | undefined, isOcppRestricted: boolean) {
  return isOcppRestricted ? [] : (reportList ?? []).filter((curItem) => curItem.type === "site");
}

export const getPanels = createSelector(
  [
    (state: IState) => state.para.parameter,
    getSite,
    getUserAccess,
    (state: IState) => state.router.rootPath,
    (state: IState) => !!state.site.user?.isRfidUser,
    (state: IState) => !IS_CLOUD && state.site.user?.secId === "admin",
    (state: IState) => isSet(state.scheduler.allData.lastData),
    getHasOcppChargers,
    getHasEnlineChargerData,
    (state: IState) => state.ocpp.pendingChargingSession.length > 0,
    (state: IState) => state.ocpp.rfid,
    (state: IState) => state.data.alarms.length > 0,
    (state: IState) => state.site.reportList,
    (state: IState) => state.cached.selectedSite,
  ],
  (
    parameter,
    site,
    accessRights,
    rootPath,
    isRfidUser,
    isLocalAdminUser,
    hasLiveData,
    hasOcppChargers,
    hasEnlineChargerData,
    hasPendingChargingSessions,
    rfidCards,
    hasAlarms,
    reportList,
    selectedSite,
  ) => {
    const isOcppRestricted = isOcppFrontend(rootPath);
    const isTrendRestricted = rootPath === RootPath.TREND;

    const panels: INavigationPanelEntry[] = [];
    const dashboards: IDashboardEntry[] = [];
    // active reports of the current site, plus the cross-site ones
    const hasReports = [
      ...getSiteReports(reportList, selectedSite, isOcppRestricted),
      ...getCrossSiteReports(reportList, isOcppRestricted),
    ].some((curItem) => curItem.isActive);

    let nrPanels = 0;
    if (!isRfidUser && hasLiveData && !isOcppRestricted) {
      panels.push({ heading: MainPanel.DASHBOARD });
      panels.push({ heading: MainPanel.POWER, subPanels: [MainPanel.ENERGYMETERS] });
      nrPanels += 2;
      dashboards.push({ entry: DashboardPanel.POWER, prio: 5 });
      if (parameter?.regulation.length) {
        panels.push({ heading: MainPanel.MONITOR });
        dashboards.push({ entry: DashboardPanel.MONITOR, prio: 0 });
        nrPanels += 1;
      }
    }

    if (!isOcppRestricted) {
      const trendPanels: INavigationPanelEntry[] = [];
      if (parameter?.tariffline && parameter?.tariffline.length > 0) {
        trendPanels.push({ heading: MainPanel.ENERGY });
        nrPanels += 1;
      }
      if (
        (site?.cloudSettings?.trendMonitor?.filter((curItem) => curItem.isActive).length ?? 0) > 0
      ) {
        trendPanels.push({ heading: MainPanel.TRENDMONITOR });
        dashboards.push({ entry: DashboardPanel.TRENDMONITOR, prio: 7 });
        nrPanels += 1;
      }
      if (
        site?.cloudSettings?.customDashboard ||
        parameter?.general.customSettings?.customDashboard
      ) {
        trendPanels.push({ heading: MainPanel.PUBLIC_DISPLAY });
        nrPanels += 1;
      }
      panels.push({
        heading: MainPanel.ENERGY,
        childPanels: trendPanels,
      });
      dashboards.push({ entry: DashboardPanel.ENERGY, prio: 1 });
    }

    if (!isOcppRestricted) {
      const journalPanels: INavigationPanelEntry[] = [];
      // check wether we have IOs at all
      const hasInput = parameter?.input.some((curItem) => curItem.webActive);
      const hasOutput = parameter?.output.some((curItem) => curItem.webActive);
      if (hasLiveData && !isOcppRestricted && (hasInput || hasOutput)) {
        journalPanels.push({ heading: MainPanel.IOS });
        nrPanels += 1;
      }
      // check wether we have trended IOs
      if (hasInput || hasOutput) {
        journalPanels.push({ heading: MainPanel.IO_TREND });
        nrPanels += 1;
      }
      // show modules and conditions?
      if ((accessRights?.controllerAdmin || isLocalAdminUser) && parameter?.module.length) {
        journalPanels.push({ heading: MainPanel.MODULES, subPanels: [MainPanel.CONDITIONDETAIL] });
      }
      panels.push({
        heading: MainPanel.IOS,
        childPanels: journalPanels,
      });

      if (hasInput) {
        dashboards.push({ entry: DashboardPanel.INPUTS, prio: 6 });
      }
      if (hasOutput) {
        dashboards.push({ entry: DashboardPanel.OUTPUTS, prio: 3 });
      }
    }

    if (!isTrendRestricted) {
      const chargerPanels: INavigationPanelEntry[] = [];
      if (IS_CLOUD && hasOcppChargers) {
        chargerPanels.push({
          heading: MainPanel.CHARGERLIST,
          subPanels: [MainPanel.CHARGERDETAIL],
        });
        nrPanels += 1;
      }
      if ((!IS_CLOUD || accessRights?.showEnlineChargers) && hasEnlineChargerData) {
        chargerPanels.push({ heading: MainPanel.ENLINE_CHARGER });
        nrPanels += 1;
      }
      // currently active charging session for RFID user?
      if (isRfidUser && hasPendingChargingSessions) {
        chargerPanels.push({ heading: MainPanel.CHARGINGSESSION });
        nrPanels += 1;
      }
      if (hasOcppChargers || isRfidUser) {
        // if we face multiple RFID users, we take the rfid panel, otherwise the plain session list
        if (rfidCards.length > 1) {
          chargerPanels.push({
            heading: MainPanel.RFIDLIST,
            subPanels: [MainPanel.RFIDDETAIL],
          });
        } else {
          chargerPanels.push({ heading: MainPanel.CHARGINGSESSIONLIST });
        }
        nrPanels += 1;
      }
      if (hasOcppChargers && (accessRights?.ocppSettings ?? false)) {
        chargerPanels.push({ heading: MainPanel.OCPPCONFIGURATION });
        nrPanels += 1;
      }
      if (chargerPanels.length > 0) {
        panels.push({
          heading: MainPanel.CHARGERLIST,
          childPanels: chargerPanels,
          subPanels: [MainPanel.CHARGINGSESSION],
        });
        // do not add dashboard panel on local controller webside as it works with ocpp chargers only
        // also, do not add the dashboard panel if there are only enline chargers
        if (IS_CLOUD && hasOcppChargers) {
          dashboards.push({ entry: DashboardPanel.EMOBILTY, prio: 2 });
        }
      }
    }
    if (!isOcppRestricted && !isTrendRestricted && !isRfidUser && (hasLiveData || hasAlarms)) {
      dashboards.push({ entry: DashboardPanel.ALARMS, prio: hasAlarms ? 4 : 10 });
    }

    if (hasReports) {
      dashboards.push({ entry: DashboardPanel.REPORTS, prio: 8 });
    }

    dashboards.push({ entry: DashboardPanel.SITEINFOS, prio: 9 });

    return { panels, nrPanels, dashboards };
  },
);

export function hasLocalSettingsPage(controllerState: IStateController | undefined) {
  return (
    !IS_CLOUD &&
    (isLocalhost() ||
      (isSet(controllerState?.unlockedUntil) && controllerState!.unlockedUntil > Date.now()))
  );
}

// true if we have a direct, live connection to the controller (local build, or cloud via proxy),
// as opposed to a plain cloud connection with only periodically pushed data
export function isDirectConnection(state: IState) {
  return !IS_CLOUD || state.scheduler.liveViaProxy;
}

function getPriorizedDashboardItems(
  available: IDashboardEntry[],
  used: (DashboardPanel | undefined)[],
) {
  const nextPanel = available
    .sort((a, b) => {
      return a.prio - b.prio;
    })
    .map((item) => item.entry)
    .find((curItem) => !used.includes(curItem));
  return nextPanel;
}

export function getActiveDashboardItems(state: IState) {
  const { panels, nrPanels, dashboards } = getPanels(state);

  const activeDashboardItems: (DashboardPanel | undefined)[] = [];
  const dashboardItems = getSiteSelection(state)?.dashboardPanel;
  for (let i = 0; i < MAX_DASHBOARDS; i++) {
    if (isSet(dashboardItems?.[i])) {
      activeDashboardItems[i] = dashboardItems[i]!;
    } else {
      // take next panel according priorities
      activeDashboardItems[i] = getPriorizedDashboardItems(dashboards, activeDashboardItems);
    }
  }

  return { dashboards, activeDashboardItems };
}

export function getSiteSelection(state: IState) {
  // site selected?
  const siteId = state.cached.selectedSite;
  if (
    !isSet(siteId) ||
    !isSet(state.cached.siteSelection) ||
    !isSet(state.cached.siteSelection[siteId])
  ) {
    return undefined;
  }
  return state.cached.siteSelection[siteId];
}

export function formatDate(
  timestampMs: number,
  timePeriod: TimePeriod,
  locales?: string | string[],
  showTime?: boolean,
) {
  let options: Intl.DateTimeFormatOptions;
  if (timePeriod === TimePeriod.YEAR) {
    options = {
      year: "numeric",
    };
  } else if (timePeriod === TimePeriod.MONTH) {
    options = {
      year: "numeric",
      month: "long",
    };
  } else if (timePeriod === TimePeriod.WEEK || timePeriod === TimePeriod.CUSTOM) {
    options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };
  } else {
    // (this.props.xZoom === TimePeriod.DAY)
    options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "long",
    };
  }

  if (showTime === true && (timePeriod === TimePeriod.DAY || timePeriod === TimePeriod.WEEK)) {
    options.hour = "numeric";
    options.minute = "numeric";
  }

  const date = new Date(timestampMs);
  return date.toLocaleString(locales, options);
}

export function getDateString(
  timePeriod: TimePeriod,
  startDate: number | undefined,
  endDate: number | undefined,
  isAtEnd: boolean,
  dateString: string,
  locales?: string | string[],
): string | undefined {
  // if we zoom all data, we have no period
  const startDateString = formatDate((startDate ?? 0) * 1000, timePeriod, locales);
  const endDateString =
    isSet(endDate) ? formatDate((endDate - 1) * 1000, timePeriod, locales) : dateString;
  if ([TimePeriod.YEAR, TimePeriod.MONTH, TimePeriod.DAY].includes(timePeriod)) {
    return isAtEnd ? endDateString : startDateString;
  } else if (TimePeriod.WEEK === timePeriod && isAtEnd) {
    return endDateString;
  }

  return isSet(startDate) ? `${startDateString} - ${endDateString}` : dateString;
}

export function getEnergySourceName(source: EnergySource | undefined) {
  if (source === "electrical" || !isSet(source)) return <FormattedMessage id="Electrical" />;
  if (source === "water") return <FormattedMessage id="Water" />;
  if (source === "gas") return <FormattedMessage id="Gas" />;
  return <>{source}</>;
}

export function id2Index(list: { id?: number }[] | undefined, id: number | undefined | null) {
  if (!isSet(id) || !isSet(list)) {
    return undefined;
  }
  const index = list.findIndex(
    (curItem, curIndex) =>
      (isSet(curItem.id) && curItem.id === id) || (!isSet(curItem.id) && curIndex === id),
  );
  if (index === -1) return undefined;
  return index;
}

// keep consistent with dbutils.ts
export function parseVirtualSiteId(virtSiteId: number) {
  return {
    siteId: virtSiteId < 10000 ? undefined : Math.floor(virtSiteId / 10000),
    itemId: virtSiteId % 10000,
  };
}

// checks whether the item at the given index within a parameter group (energymeter, tariffline,
// input, output,...) actually originates from another (virtual) site
export function isVirtualSiteItem(
  virtualAssigns: IVirtAssign[] | undefined,
  index: number,
  ownSiteId: number | undefined,
) {
  return (
    virtualAssigns?.some(
      (curAssign) => curAssign.destIndex === index && curAssign.sourceSite !== ownSiteId,
    ) ?? false
  );
}

// checks whether the given OCPP charger id (t_charger DB id, i.e. fk_charger) originates from
// another (virtual) site; the "ocpp" group is addressed differently than the other virtualSite
// groups: destIndex is always undefined there, and sourceIndex holds the source charger id
// itself instead of a destination array index (see IVirtAssign / dbutils.ts)
export function isVirtualOcppCharger(
  virtualOcppAssigns: IVirtAssign[] | undefined,
  chargerId: number | undefined,
  ownSiteId: number | undefined,
) {
  return (
    virtualOcppAssigns?.some(
      (curAssign) => curAssign.sourceIndex === chargerId && curAssign.sourceSite !== ownSiteId,
    ) ?? false
  );
}

// resolves the display name of a site by id, for use in the virtual-site indicator tooltip;
// falls back to a "#<id>" placeholder if the site is not in the given list (e.g. not accessible
// to the current user)
export function getSiteName(siteId: number, siteList: Site[] | undefined) {
  return siteList?.find((curSite) => curSite.id === siteId)?.name ?? `#${siteId}`;
}

// module type definitions (from enplanconfig.json) list a channel name per index; some module
// types (e.g. gateways) instead just declare a channel count, so no name is available there
interface IModuleConfigEntry {
  id: string;
  channels?: {
    input?: string[] | number;
    output?: string[] | number;
    energymeter?: string[] | number;
  };
}

// resolves the channel name of an input/output/energymeter within its module, by looking up the
// module's type in enplanconfig.json; falls back to the (1-based) channel number if the module
// type only declares a channel count instead of individual names (e.g. gateways)
export function getModuleChannelName(
  moduleType: string | undefined,
  channel: number,
  kind: "input" | "output" | "energymeter",
): string {
  const moduleDef = (moduleConfig.modules as IModuleConfigEntry[]).find(
    (curItem) => curItem.id === moduleType,
  );
  const channelNames = moduleDef?.channels?.[kind];
  return Array.isArray(channelNames) ? channelNames[channel] : `${channel + 1}`;
}

// resolves the display name of the site a virtual parameter group item originates from, for use
// in the virtual-site indicator tooltip; returns undefined if the item is not virtual
export function getVirtualSiteSourceName(
  virtualAssigns: IVirtAssign[] | undefined,
  index: number,
  ownSiteId: number | undefined,
  siteList: Site[] | undefined,
) {
  const sourceSiteId = virtualAssigns?.find(
    (curAssign) => curAssign.destIndex === index && curAssign.sourceSite !== ownSiteId,
  )?.sourceSite;
  if (!isSet(sourceSiteId)) return undefined;
  return getSiteName(sourceSiteId, siteList);
}

// same as getVirtualSiteSourceName, but for the differently-addressed "ocpp" virtualSite group
export function getVirtualOcppChargerSourceName(
  virtualOcppAssigns: IVirtAssign[] | undefined,
  chargerId: number | undefined,
  ownSiteId: number | undefined,
  siteList: Site[] | undefined,
) {
  const sourceSiteId = virtualOcppAssigns?.find(
    (curAssign) => curAssign.sourceIndex === chargerId && curAssign.sourceSite !== ownSiteId,
  )?.sourceSite;
  if (!isSet(sourceSiteId)) return undefined;
  return getSiteName(sourceSiteId, siteList);
}

// renders the small indicator icon used to mark list entries whose data originates from
// another (virtual) site; wrapped in a span since SVGProps has no "title" attribute
export function renderVirtualSiteIcon(title: string) {
  return (
    <span title={title}>
      <IconVirtualSite className="virtual-site-icon" />
    </span>
  );
}

export function getIdentifierForMainPanel(inPanel: MainPanel | undefined): PeriodIdentfier {
  const isChargerFilter =
    isSet(inPanel) &&
    [
      MainPanel.CHARGERLIST,
      MainPanel.CHARGINGSESSION,
      MainPanel.CHARGINGSESSIONLIST,
      MainPanel.CHARGERDETAIL,
      MainPanel.RFIDLIST,
      MainPanel.RFIDDETAIL,
    ].includes(inPanel);

  const isIoFilter = isSet(inPanel) && [MainPanel.IOS, MainPanel.IO_TREND].includes(inPanel);

  const identifier: PeriodIdentfier =
    isChargerFilter ? "chargingSessionFilter"
    : isIoFilter ? "ioPeriod"
    : "trendPeriod";

  return identifier;
}

export function getUpdateCommandForIdentifier(inIdentifier: PeriodIdentfier) {
  return (
    inIdentifier === "trendPeriod" ? ActionType.ACT_UPDATE_TREND
    : inIdentifier === "ioPeriod" ? ActionType.ACT_UPDATE_IOTREND
    : ActionType.ACT_GET_OCPP_SESSIONS_REQ
  );
}

export function checkCategories(
  inCategory: Category | undefined,
  inCategories: Category[] | undefined,
): boolean {
  return (
    inCategory === undefined ||
    (inCategory === null &&
      (!isSet(inCategories) || inCategories.length === 0 || inCategories.includes(""))) ||
    inCategories?.includes(inCategory ?? "") === true
  );
}

/**
 * Get all company IDs in the hierarchy under (and including) a given company.
 * This traverses the company tree using parentId relationships.
 */
function getCompanyIdsInHierarchy(
  companyId: number,
  companyList: { id: number; parentId: number | null }[],
): Set<number> {
  const result = new Set<number>([companyId]);
  let foundNew = true;

  // Keep adding children until no new companies are found
  while (foundNew) {
    foundNew = false;
    for (const company of companyList) {
      if (company.parentId !== null && result.has(company.parentId) && !result.has(company.id)) {
        result.add(company.id);
        foundNew = true;
      }
    }
  }

  return result;
}

/**
 * Get all site IDs a user has access to based on:
 * 1. siteAccess right being true - grants access to all sites in user's company hierarchy
 * 2. Sites belonging to user's company or any child company in the hierarchy
 *
 * @param user - The user object with companyId and accessRights.siteAccess
 * @param siteList - All available sites
 * @param companyList - All companies with their parentId relationships
 * @returns Array of site IDs the user has access to
 */
export function getAccessibleSiteIds(
  user: { companyId: number; accessRights: { siteAccess?: boolean } },
  siteList: { id: number; companyId: number }[],
  companyList: { id: number; parentId: number | null }[],
): number[] {
  // If user doesn't have siteAccess right, they have no automatic site access
  if (!user.accessRights.siteAccess) {
    return [];
  }

  // Get all companies in the user's hierarchy (their company + all descendants)
  const accessibleCompanyIds = getCompanyIdsInHierarchy(user.companyId, companyList);

  // Return all sites that belong to any of these companies
  return siteList.filter((site) => accessibleCompanyIds.has(site.companyId)).map((site) => site.id);
}

// triggers a browser download of the given text content as a local file
export function downloadTextFile(content: string, filename: string, mimeType = "application/json") {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function getInitials(name: string | undefined): string {
  if (!name) return "?";
  // ignore separators like "|" or "&", trailing words are often ids or locations
  const words = name.split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 0);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return (words[0] ?? name).substring(0, 2).toUpperCase();
}
